/* Build in libraries */

import { useState } from 'react';
import { useLocation } from 'react-router';
/* Components */
import Card from '@/components/UI/Card/index.tsx';
import StatCard from '@/components/UI/Card/StatCard';

interface IApplicationDashboardComponentProps {
  data: any;
  defaultView?: 'ALL' | 'APPROVED';
  hideSwitch?: boolean;
}

export const ApplicationDashboard = ({ data, defaultView = 'ALL', hideSwitch }: IApplicationDashboardComponentProps) => {
  const _location = useLocation();
  const [_selectedProject, _setSelectedProject] = useState<any>();
  const [viewStatus, setViewStatus] = useState(defaultView);

  const applicationsList = data?.data;

  const widgetsData: {
    statusSummary: { [key: string]: number };
    totalProjectProposed: number;
    valueOfProposedProjects: number;
    totalCoBLandPurchases: number;
    totalUnitsProposed: number;
    cityFundsRequested: number;
    totalForSaleUnitsProposed: number;
    totalAffdForSaleUnitsProposed: number;
    totalAffdRentalUnitsProposed: number;
    totalMarketRentalUnitsProposed: number;
    minorityDevelopers: number;
    developerInvestmentsProposed: number;
    applicationLocationList: Array<{
      id: string;
      [key: string]: string;
      fullAddress: string;
    }>;
  } = {
    statusSummary: {},
    totalProjectProposed: 0,
    valueOfProposedProjects: 0,
    totalCoBLandPurchases: 0,
    totalUnitsProposed: 0,
    cityFundsRequested: 0,
    totalForSaleUnitsProposed: 0,
    totalAffdForSaleUnitsProposed: 0,
    totalAffdRentalUnitsProposed: 0,
    totalMarketRentalUnitsProposed: 0,
    minorityDevelopers: 0,
    developerInvestmentsProposed: 0,
    applicationLocationList: [],
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData, id } = a;

    // Location
    const applicationLocation = `${applicationData?.propertyDetails?.streetAddress}, ${applicationData?.propertyDetails?.city}, ${applicationData?.propertyDetails?.state}, ${applicationData?.propertyDetails?.zipCode}`;
    widgetsData.applicationLocationList.push({
      id: id,
      email: applicationData?.applicantInformation?.contactEmail,
      name: applicationData?.applicantInformation?.fullName,
      phone: applicationData?.applicantInformation?.phone,
      fullAddress: applicationLocation || '',
    });

    //Status Summary
    widgetsData.statusSummary[a.status] = (widgetsData.statusSummary[a.status] ?? 0) + 1;

    // Total Project Proposed
    widgetsData.totalProjectProposed += 1;

    // Value of Proposed Projects
    widgetsData.valueOfProposedProjects += applicationData?.projectDetails?.developmentCostEstimate;

    applicationData?.projectFinance?.sources?.forEach((s: any) => {
      //City Funds Requested
      if (s?.name === 'City of Birmingham') {
        widgetsData.cityFundsRequested += s?.amount;
      }

      // Developer Investments Proposed
      if (s?.name === 'Developer Equity') {
        widgetsData.developerInvestmentsProposed += s?.amount;
      }
    });

    if (applicationData?.leadershipInformation?.hasMajorityOwnership === 'yes') {
      widgetsData.minorityDevelopers += 1;
    }

    // Total Units Proposed
    widgetsData.totalUnitsProposed += applicationData?.projectDetails?.proposedUnits ?? 0;

    // Total CoB Land Purchases
    if (applicationData?.projectDetails?.isPropertyCityOwn === 'yes') {
      widgetsData.totalCoBLandPurchases += applicationData?.projectDetails?.proposedAmount;
    }

    // Total Aff. For-Sale Units Proposed
    if (applicationData?.projectDetails?.projectType === 'Affordable Homeownership') {
      widgetsData.totalAffdForSaleUnitsProposed += applicationData?.projectDetails?.proposedUnits;
    }

    // Total Aff. Rental Units Proposed
    if (applicationData?.projectDetails?.projectType === 'Affordable Rental Housing: 4%, 9%') {
      widgetsData.totalAffdRentalUnitsProposed += applicationData?.projectDetails?.proposedUnits;
    }

    // Total For-Sale Units Proposed
    if (applicationData?.projectDetails?.projectType === 'Home Ownership') {
      widgetsData.totalForSaleUnitsProposed += applicationData?.projectDetails?.proposedUnits;
    }

    // Total Market Rental Units Proposed
    if (applicationData?.projectDetails?.projectType === 'Market Rate Multifamily') {
      widgetsData.totalMarketRentalUnitsProposed += applicationData?.projectDetails?.proposedUnits;
    }
  });

  // const fetchApplicationLocation = useQueries({
  //   queries: widgetsData?.applicationLocationList?.map(
  //     (d: { id: string; [key: string]: string; fullAddress: string }) => {
  //       return {
  //         queryKey: ['GET_APPLICATION_WIDGET_GEO_CODE', d?.id],
  //         queryFn: () => getAddressDecode(d.fullAddress),
  //         select: (resData: { data: IGetAddressDecodeResData }) => {
  //           const { lat, lng } = resData.data;
  //           return { ...d, coordinates: { lat, lng } };
  //         },
  //       };
  //     }
  //   ),
  // });
  // const applicationsGeoLocated = fetchApplicationLocation?.map((d) => d.data);
  return (
    <div className='max-w-screen-3xl mx-auto'>
      {!hideSwitch && (
        <div className='flex font-semibold text-bold rounded-md space-x-2'>
          <div
            className={`${
              viewStatus === 'ALL' ? 'bg-primaryColor-normal' : 'bg-neutralColor-light hover:bg-primaryColor-normal'
            } text-white px-4 py-2 cursor-pointer`}
            onClick={() => setViewStatus('ALL')}
          >
            All Submissions
          </div>
          <div
            className={`${
              viewStatus === 'APPROVED' ? 'bg-primaryColor-normal' : 'bg-neutralColor-light hover:bg-primaryColor-normal'
            } text-white px-4 py-2 cursor-pointer`}
            onClick={() => setViewStatus('APPROVED')}
          >
            Approved Submissions
          </div>
        </div>
      )}
      {viewStatus && (
        <div className='space-y-4 py-4'>
          <section className='grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
            <StatCard title='Total Inventory (Units)' stat={3464} />
            <StatCard title='Units Subject to R.S' stat={1764} />
            <StatCard title='Median Rent Increase for R.S Units' stat={'$294'} />
            <StatCard title='Average R.S Occupancy' stat={'91%'} />
          </section>
          {/* <section>
              <Card title='R.S Units on Map'>
                {applicationsGeoLocated.length === 0 && (
                  <div className='w-full flex flex-col justify-center items-center h-screen/2'>
                    <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                      <MapPinIcon className='h-8 w-8' />
                      <h1>No Projects</h1>
                    </div>
                    <p>There are no assigned projects to display.</p>
                  </div>
                )}
                {applicationsGeoLocated.length > 0 && (
                  <div className='w-full grid grid-cols-6 h-screen/2'>
                    <div className='col-span-6 w-full border-l p-5'>
                      {applicationsGeoLocated[0] && (
                        <GoogleMap
                          defaultCenter={{
                            lat: applicationsGeoLocated[0].coordinates.lat,
                            lng: applicationsGeoLocated[0].coordinates.lng,
                          }}
                        >
                          {applicationsGeoLocated?.map((res: any) => {
                            return (
                              <AdvancedMarker
                                key={res?.id}
                                position={{
                                  lat: res?.coordinates?.lat,
                                  lng: res?.coordinates?.lng,
                                }}
                              >
                                <div className='relative'>
                                  {selectedProject &&
                                    selectedProject?.id === res?.id && (
                                      <div
                                        onClick={() =>
                                          setSelectedProject(undefined)
                                        }
                                        className='bg-white border rounded absolute p-3 bottom-4 left-5 w-56'
                                      >
                                        <XCircleIcon className='h-4 w-4 absolute right-1 top-1 text-neutralColor-normal hover:text-red-500' />
                                        <Link
                                          to={`${location.pathname.replace(
                                            'dashboard',
                                            'applications'
                                          )}/${
                                            selectedProject?.id
                                          }/application_details`}
                                        >
                                          <p className='font-semibold text-primaryColor-normal underline'>
                                            {selectedProject?.name}
                                          </p>
                                        </Link>
                                        <p className='text-xs'>
                                          {selectedProject?.fullAddress}
                                        </p>
                                        <div className='mt-2 text-sm flex justify-between gap-2'>
                                          <span>Email</span>
                                          <span className='font-semibold'>
                                            {selectedProject?.email}
                                          </span>
                                        </div>
                                        <div className='mt-2 text-sm flex justify-between gap-2'>
                                          <span>Phone</span>
                                          <span className='font-semibold'>
                                            <PhoneNumberFormatDisplay
                                              value={
                                                selectedProject?.phone || 'none'
                                              }
                                            />
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  {res && (
                                    <MapPinIcon
                                      onClick={() => setSelectedProject(res)}
                                      className={
                                        selectedProject?.id === res?.id
                                          ? 'h-8 w-8 text-black'
                                          : 'h-8 w-8 text-red-500 '
                                      }
                                    />
                                  )}
                                </div>
                              </AdvancedMarker>
                            );
                          })}
                        </GoogleMap>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            </section> */}
          <section className='grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4'>
            <div className='col-span-4 2xl:col-span-2'>
              <Card title='Unit Type Availability'>
                <div className='w-full h-full'>
                  <table className='divide-y divide-gray-200 w-full'>
                    <thead>
                      <tr className='text-left bg-gray-50'>
                        <th className='px-6 py-4'>Unit Type</th>
                        <th className='px-6 py-4'>Available Units</th>
                      </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-gray-200'>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>Studio</th>
                        <td className='px-6 py-4'>12</td>
                      </tr>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>1</th>
                        <td className='px-6 py-4'>8</td>
                      </tr>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>2</th>
                        <td className='px-6 py-4'>5</td>
                      </tr>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'> 3</th>
                        <td className='px-6 py-4'>2</td>
                      </tr>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'> 4</th>
                        <td className='px-6 py-4'>9</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
            <div className='col-span-4 2xl:col-span-2'>
              <Card title='R.S Units by Neighborhood'>
                <div className='w-full h-full'>
                  <table className='divide-y divide-gray-200 w-full'>
                    <thead>
                      <tr className='text-left bg-gray-50'>
                        <th className='px-6 py-4'>Neighborhood</th>
                        <th className='px-6 py-4'>Units Available</th>
                      </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-gray-200'>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>
                          College Heights Estates
                        </th>
                        <td className='px-6 py-4'>5</td>
                      </tr>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>
                          Ellaville
                        </th>
                        <td className='px-6 py-4'>8</td>
                      </tr>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>
                          Historic District
                        </th>
                        <td className='px-6 py-4'>2</td>
                      </tr>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>
                          Hyattsville Hills
                        </th>
                        <td className='px-6 py-4'>0</td>
                      </tr>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>
                          International Corridor
                        </th>
                        <td className='px-6 py-4'>3</td>
                      </tr>
                      <tr className='hover:bg-green-50 cursor-pointer'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>
                          Kirkwood
                        </th>
                        <td className='px-6 py-4'>4</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};
